<script setup lang="ts">
type DownloadProps = {
  files: File[]
  lineId?: number | null
  cardBackgroundClass?: string
}

const props = withDefaults(defineProps<DownloadProps>(), {
  cardBackgroundClass: 'bg-white',
})

const fileGroups = computed(() => {
  // TODO: Get the tags from the API
  const tags = {
    manuals: [47624, 39701, 19],
    technical: [34067, 33562],
    gallery: [17, 33876],
  }
  const groups = {}
  Object.entries(tags).forEach((entry) => {
    const [key, tagIds] = entry
    groups[key] = {
      tagIds,
      files: props.files.filter((file) =>
        file.fileTags.some((tag) => tagIds.includes(tag.translationOfNid))
      ),
    }
  })
  return groups
})

const fileGroupsFilter = ref({
  manuals: false,
  technical: false,
  gallery: false,
})

const MAX_FILES = 5

const localePath = useLocalePath()

const downloadsLink =
  localePath({ name: 'download' }) + '?p[]=' + props.lineId?.toString()
</script>

<template>
  <UiSection
    v-if="props.files.length"
    gray
    :title="$t('productline.downloadFiles.title')"
  >
    <template v-if="props.files.length">
      <div
        class="grid grid-cols-1 gap-5 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3"
      >
        <div
          v-for="[key, value] in Object.entries(fileGroups)"
          :key="key"
          class="flex h-max flex-col gap-4 rounded-sm px-4 py-8"
          :class="props.cardBackgroundClass"
        >
          <h3 class="font-pragmatica-ext ml-2 text-2xl font-bold">
            {{ $t('base.download.tagGroups.' + key) }}
          </h3>
          <div class="flex flex-col">
            <DownloadFilePreview
              v-for="file in value.files.slice(
                0,
                fileGroupsFilter[key] ? undefined : MAX_FILES
              )"
              :key="file.nid"
              :file="file"
              inline
            />
            <UiAnimatedUnderlineText
              v-if="value.files.length > MAX_FILES"
              class="mx-auto mt-5 w-fit"
            >
              <button
                @click="
                  () => {
                    fileGroupsFilter[key] = !fileGroupsFilter[key]
                  }
                "
              >
                <span v-if="!fileGroupsFilter[key]">
                  {{ $t('base.download.show.allFiles') }}
                </span>
                <span v-else>{{ $t('base.download.show.less') }}</span>
              </button>
            </UiAnimatedUnderlineText>
          </div>
        </div>
      </div>
      <div class="flex justify-center pt-10">
        <NuxtLink
          :to="downloadsLink"
          class="group relative no-underline transition-all"
        >
          <UiAnimatedUnderlineText>
            <template #text>
              {{ $t('base.download.toAllFiles') }}
            </template>
          </UiAnimatedUnderlineText>
        </NuxtLink>
      </div>
    </template>
    <template v-else>
      <div class="font-pragmatica-ext font-bold">
        {{ $t('base.download.results.noFiles') }}
      </div>
    </template>
  </UiSection>
</template>
